import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input, Button, useToast } from "../design-system";
import { signInFailureMessage } from "../lib/signInMessage";

/* Shield / clipboard icon for branding */
function LogoIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="12" fill="var(--color-primary)" />
      <path d="M16 18h16M16 24h16M16 30h10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M30 28l3 3 5-6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* Face ID glyph for the passkey sign-in button */
function FaceIdIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2" />
      <path d="M9 10v1M15 10v1M12 10v3.5a1 1 0 0 1-1 1h-.5" />
      <path d="M9 16.5s1 1 3 1 3-1 3-1" />
    </svg>
  );
}

/* Google "G" logo for the Google sign-in button */
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

function LoginPage() {
  const auth = useAuth();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Set when the server refuses login because the email isn't verified yet,
  // so we can show a "verify first / resend" banner instead of a generic error.
  const [needsVerify, setNeedsVerify] = useState(null);
  const [resending, setResending] = useState(false);
  // Persistent failure message (disabled account, lockout, wrong password)
  const [error, setError] = useState(null);
  // Google sign-in failure reason, read once from ?google_error= on the way
  // back from /api/auth/google/callback (that route can't render React).
  const [googleError, setGoogleError] = useState(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    return params.get("google_error");
  });
  // Face ID / passkey (additive — never blocks the password form below)
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const canPasskey =
    typeof auth.passkeySupported === "function" ? auth.passkeySupported() : false;

  const GOOGLE_ERROR_MESSAGES = {
    no_account: "We don't see a Butler Core account for that Google email yet. Please sign up first, then connect Google.",
    email_not_verified: "That Google account's email isn't verified. Try a different Google account.",
    account_disabled: "This account is disabled. Please contact your administrator.",
    not_configured: "Google sign-in isn't set up yet. Please use your email and password.",
    bad_state: "That Google sign-in attempt expired. Please try again.",
  };
  const googleErrorMessage = googleError
    ? (GOOGLE_ERROR_MESSAGES[googleError] || "Google sign-in didn't work. Please use your email and password.")
    : null;

  // Where to land after signing in. Modules outside this React app (Water, Comms,
  // On-Call ...) bounce signed-out visitors here as /app/login?next=/water/ so the
  // person comes back to the page they actually wanted. Only same-site absolute
  // paths are honored — never an off-site URL, and never a protocol-relative one.
  const nextUrl = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    const raw = new URLSearchParams(window.location.search).get("next");
    if (!raw || !/^\/[^/\\]/.test(raw)) return null;
    return raw;
  }, []);

  // Land on the Butler Core launcher (tile page) — NOT the legacy '/app/' URL,
  // which points at the pre-front-door static build and bypasses the suite.
  if (auth.isAuthenticated) {
    // ?next= points outside this router's basename, so it needs a real navigation.
    if (nextUrl) { window.location.replace(nextUrl); return null; }
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setLoading(true);
    setNeedsVerify(null);
    setError(null);
    try {
      await auth.login(email, password);
      toast.success("Welcome back!");
    } catch (err) {
      if (err.code === "EMAIL_NOT_VERIFIED") {
        setNeedsVerify(err.email || email);
        toast.error("Please verify your email before signing in.");
      } else {
        // Show a clear, persistent reason instead of the button just spinning.
        // ONE owner for this sentence (lib/signInMessage.js) so this page and
        // the in-place "Sign in again" card can never drift apart, and neither
        // ever says which half of the credentials was wrong.
        const friendly = signInFailureMessage(err);
        setError(friendly);
        toast.error(friendly);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasskey = async () => {
    setError(null);
    setNeedsVerify(null);
    setPasskeyLoading(true);
    try {
      await auth.loginWithPasskey();
      toast.success("Welcome back!");
      // auth.isAuthenticated flips true → the <Navigate> above sends us to the hub.
    } catch (err) {
      const name = err && err.name;
      const msg = (err && err.message) || "";
      // User simply dismissed the OS prompt — stay quiet, leave the form ready.
      if (name === "NotAllowedError" || name === "AbortError" || /cancel|abort|timed out/i.test(msg)) {
        // no-op
      } else {
        toast.error("Face ID sign-in didn't work. Use your email and password below.");
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await auth.resendVerification(needsVerify);
      toast.success("Verification email sent. Check your inbox.");
    } catch (err) {
      toast.error(err.message || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "var(--space-6) var(--space-4)",
      backgroundColor: "var(--surface-page)",
    }}>
      <div style={{
        width: "100%",
        maxWidth: "400px",
      }}>
        {/* Logo + Title */}
        <div style={{ textAlign: "center", marginBottom: "var(--space-8)" }}>
          <div style={{ display: "inline-flex", marginBottom: "var(--space-4)" }}>
            <LogoIcon />
          </div>
          <h1 style={{
            fontSize: "var(--text-h2)",
            fontWeight: "var(--weight-bold)",
            color: "var(--text-primary)",
            margin: "0 0 var(--space-1_5)",
            fontFamily: "var(--font-family)",
            letterSpacing: "var(--tracking-tight)",
          }}>
            Sign in
          </h1>
          <p style={{
            fontSize: "var(--text-body-md)",
            color: "var(--text-secondary)",
            margin: 0,
            fontFamily: "var(--font-family)",
          }}>
            Welcome back to Butler Core — one login, every Butler.
          </p>
        </div>

        {/* Unverified-email banner — only after a login blocked for verification */}
        {needsVerify && (
          <div style={{
            marginBottom: "var(--space-5)",
            padding: "var(--space-4)",
            borderRadius: "var(--radius-lg)",
            backgroundColor: "var(--color-warning-50, #fff8e6)",
            border: "1px solid var(--color-warning-200, #ffe08a)",
            fontFamily: "var(--font-family)",
          }}>
            <p style={{ margin: "0 0 var(--space-2)", fontSize: "var(--text-body-sm)", color: "var(--text-primary)" }}>
              Your email <strong>{needsVerify}</strong> isn't verified yet. Click the link we emailed you, or send a new one.
            </p>
            <Button variant="secondary" size="sm" loading={resending} onClick={handleResend}>
              Resend verification email
            </Button>
          </div>
        )}

        {/* Persistent error banner — clear reason a sign-in failed */}
        {error && (
          <div role="alert" style={{
            marginBottom: "var(--space-5)",
            padding: "var(--space-4)",
            borderRadius: "var(--radius-lg)",
            backgroundColor: "var(--color-danger-50, #fdecea)",
            border: "1px solid var(--color-danger-200, #f5b5ae)",
            color: "var(--color-danger-700, #8a1c12)",
            fontFamily: "var(--font-family)",
            fontSize: "var(--text-body-sm)",
          }}>
            {error}
          </div>
        )}

        {/* Google sign-in failure banner — shown once after a failed round trip */}
        {googleErrorMessage && (
          <div role="alert" style={{
            marginBottom: "var(--space-5)",
            padding: "var(--space-4)",
            borderRadius: "var(--radius-lg)",
            backgroundColor: "var(--color-danger-50, #fdecea)",
            border: "1px solid var(--color-danger-200, #f5b5ae)",
            color: "var(--color-danger-700, #8a1c12)",
            fontFamily: "var(--font-family)",
            fontSize: "var(--text-body-sm)",
          }}>
            {googleErrorMessage}
          </div>
        )}

        {/* Google sign-in (LINK ONLY — existing accounts only, never creates one) */}
        <div style={{ marginBottom: "var(--space-5)" }}>
          <Button
            type="button"
            variant="secondary"
            size="xl"
            fullWidth
            leftIcon={<GoogleIcon />}
            onClick={() => { window.location.href = auth.getGoogleLoginUrl(); }}
          >
            Sign in with Google
          </Button>
        </div>

        {/* Face ID / passkey sign-in (only shown when the browser supports it) */}
        {canPasskey && (
          <div style={{ marginBottom: "var(--space-5)" }}>
            <Button
              type="button"
              variant="secondary"
              size="xl"
              fullWidth
              loading={passkeyLoading}
              onClick={handlePasskey}
              leftIcon={<FaceIdIcon />}
            >
              Sign in with Face ID
            </Button>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-3)",
              margin: "var(--space-5) 0 0",
              color: "var(--text-secondary)",
              fontSize: "var(--text-body-sm)",
              fontFamily: "var(--font-family)",
            }}>
              <span style={{ flex: 1, height: 1, background: "var(--border-default, #e2e2e6)" }} />
              or use your email and password
              <span style={{ flex: 1, height: 1, background: "var(--border-default, #e2e2e6)" }} />
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          <div style={{ marginBottom: "var(--space-4)" }}>
            <Input
              label="Email"
              type="email"
              size="lg"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="email"
            />
          </div>

          <div style={{ marginBottom: "var(--space-2)" }}>
            <Input
              label="Password"
              type="password"
              size="lg"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              autoComplete="current-password"
            />
          </div>

          <div style={{
            textAlign: "right",
            marginBottom: "var(--space-5)",
          }}>
            <Link to="/forgot-password" style={{
              color: "var(--color-primary)",
              fontSize: "var(--text-body-sm)",
              textDecoration: "none",
              fontWeight: "var(--weight-medium)",
              fontFamily: "var(--font-family)",
            }}>
              Forgot your password?
            </Link>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="xl"
            fullWidth
            loading={loading}
          >
            Sign In
          </Button>
        </form>

        {/* Switch to register */}
        <p style={{
          textAlign: "center",
          marginTop: "var(--space-6)",
          fontSize: "var(--text-body-sm)",
          color: "var(--text-secondary)",
          fontFamily: "var(--font-family)",
        }}>
          Don&apos;t have an account?{" "}
          <Link to="/register" style={{
            color: "var(--color-primary)",
            fontWeight: "var(--weight-semibold)",
            textDecoration: "none",
          }}>
            Get started
          </Link>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
